import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Redirect, RouteProps, useRouteMatch } from 'react-router-dom';
import { UserClaims } from '@okta/okta-auth-js';
import { SecureRoute, useOktaAuth } from '@okta/okta-react';

import { useAuthContext } from '../contexts';
import { ErrorBoundaryHoc, withAppLayout } from '../hocs';
import { Role } from '../types';
import { toastFlashMessage } from '../utils';
import datadog from '../utils/datadog';
import { clearLocalStorage } from '@/utils/storage';

interface PrivateRouteProps extends RouteProps {
  title: string;
  roles: Role[];
}

const PrivateRoute: FunctionComponent<PrivateRouteProps> = ({
  title,
  roles,
  component,
  ...routeProps
}) => {
  const { user: cspUser, roles: userRoles = [], profile } = useAuthContext();
  const { oktaAuth, authState } = useOktaAuth();
  const match = useRouteMatch(routeProps);
  const pendingVerification = useRef(false);
  const latestUser = useRef<UserClaims>();
  const WrappedComponent = useMemo(
    () =>
      withAppLayout(ErrorBoundaryHoc(component), {
        title,
        roles,
      }),
    [title, roles, component, userRoles]
  );
  const isActive =
    profile?.status?.toLowerCase() === 'active' ||
    routeProps.location?.pathname.startsWith('/user/csp');

  const logout = useCallback(() => {
    clearLocalStorage();
    oktaAuth.signOut();
  }, [oktaAuth]);

  const handleVerification = async () => {
    if (pendingVerification.current || latestUser.current) {
      return;
    }
    pendingVerification.current = true;
    latestUser.current = await oktaAuth.getUser();

    const user = latestUser.current;
    if (user) {
      if (!datadog.hasUser()) {
        datadog.setUser(user);
      }

      const latestRole =
        (user.groups && (user.groups[0] as Role)) || Role.Viewer;
      const latestCspUid = user.cspUid ?? '';
      if (
        profile?.status?.toLowerCase() === 'active' &&
        (!userRoles.includes(latestRole) || latestCspUid !== profile.uid) &&
        !routeProps.location?.pathname?.startsWith('/user/csp')
      ) {
        datadog.addAction('identityVerification', {
          currentRoles: userRoles,
          newRole: latestRole,
          currentCspUid: profile.uid,
          newCspUid: latestCspUid,
          cspUserData: cspUser,
          cspProfileData: profile,
          oktaUserData: user,
        });
        /*
        toastFlashMessage(
          'Re-login required due to user role update',
          'success'
        );
        handleLogout();
         */
      }
    } else {
      logout();
    }
    pendingVerification.current = false;
  };

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) {
        return;
      }
      oktaAuth.authStateManager.updateAuthState();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [oktaAuth]);

  useEffect(() => {
    if (!match) {
      return;
    }

    if (authState?.isAuthenticated) {
      handleVerification();
    }
  }, [authState?.isAuthenticated, match]);

  return isActive ? (
    <SecureRoute
      {...routeProps}
      component={
        authState?.isAuthenticated && userRoles.length > 0
          ? WrappedComponent
          : undefined
      }
    />
  ) : (
    <Redirect to="/" />
  );
};

export default PrivateRoute;
